// /parts/scrap-bin: everything sitting at Scrap, oldest first.
//
// Two ways out. Thrown out says the box went in the dumpster and takes it off
// the books. Harvest says somebody pulled a good part out of it and moves the
// units to a shelf. Oldest first because age is the pressure on this page.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { buttonClass } from '../../components/ui/Button';
import type { ColumnDef } from '@tanstack/react-table';
import { useToast } from '../../components/Toast/ToastContext';
import PageHeader from '../../components/PageHeader';
import PageLayout from '../../components/ui/PageLayout';
import { DataTable } from '../../components/table/DataTable';
import { Checkbox } from '../../components/ui/Checkbox';
import { Tag } from '../../components/ui/Tag';
import { Tooltip } from '../../components/ui/Tooltip';
import ItemName, { itemLabel } from '../../components/ItemName';
import { Alert } from '../../components/ui/Alert';
import Reveal from '../../components/ui/Reveal';
import { EmptyState } from '../../components/ui/EmptyState';
import { LogoSpinner } from '../../components/ui/LogoSpinner';
import { formatTimestamp } from '../../utils/date-utils';
import { formatCents } from '../../Procurement/poApi';
import KeyPopover from './KeyPopover';
import MoveForm from './MoveForm';
import { usePullsApi, type ScrapBinItem } from './pullsApi';

const OLD_AFTER_DAYS = 30;

const ORIGIN_LABELS: Record<ScrapBinItem['origin'], string> = {
  receiving: 'Rejected at receiving',
  removal: 'Removed from a drone',
  pull: 'Scrap pull',
  other: 'Other',
};

function daysSince(iso: string): number {
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? Math.max(0, Math.floor((Date.now() - t) / 86400000)) : 0;
}

const TONAL_DANGER = buttonClass('danger');
const ROW_KEY = buttonClass('quiet', 'sm');

interface BinMeta {
  selected: ReadonlySet<string>;
  allSelected: boolean;
  onToggle: (id: string) => void;
  onToggleAll: () => void;
  onHarvested: (item: ScrapBinItem, moved: number, to: string) => void;
}

const BIN_COLUMNS: ColumnDef<ScrapBinItem, any>[] = [
  {
    id: 'select',
    meta: { align: 'center' },
    header: ({ table }) => {
      const { allSelected, onToggleAll } = table.options.meta as BinMeta;
      return (
        <Checkbox
          size="sm"
          checked={allSelected}
          onChange={onToggleAll}
          aria-label={allSelected ? 'Clear the selection' : 'Select every box in the bin'}
        />
      );
    },
    cell: ({ row, table }) => {
      const { selected, onToggle } = table.options.meta as BinMeta;
      const it = row.original;
      return (
        <Checkbox
          size="sm"
          checked={selected.has(it.receipt_item_id)}
          onChange={() => onToggle(it.receipt_item_id)}
          aria-label={`Select ${itemLabel(it)}, box ${it.barcode}`}
        />
      );
    },
  },
  {
    id: 'item',
    header: 'Item',
    cell: ({ row }) => (
      <>
        <ItemName size="sm" item={row.original} className="max-w-xs" />
        <span className="block font-mono text-[11px] text-on-surface-variant">{row.original.barcode}</span>
      </>
    ),
  },
  {
    id: 'units',
    header: 'Units',
    accessorFn: (it) => it.quantity_remaining,
    meta: { align: 'right', numeric: true, cellClass: 'text-on-surface' },
  },
  {
    id: 'binned',
    header: 'Binned',
    meta: { cellClass: 'whitespace-nowrap' },
    cell: ({ row }) => {
      const days = daysSince(row.original.binned_at);
      return (
        <>
          <span className="block text-on-surface">{formatTimestamp(row.original.binned_at)}</span>
          <span className={`block text-xs tabular-nums ${days > OLD_AFTER_DAYS ? 'text-amber-300' : 'text-on-surface-variant'}`}>
            {days} day{days === 1 ? '' : 's'} in the bin
          </span>
        </>
      );
    },
  },
  {
    id: 'from',
    header: 'From',
    cell: ({ row }) => (
      <>
        <span className="block text-on-surface">{ORIGIN_LABELS[row.original.origin]}</span>
        {row.original.origin_note && (
          <Tooltip label={row.original.origin_note} truncated>
            <span className="block max-w-xs truncate text-xs text-on-surface-variant">
              {row.original.origin_note}
            </span>
          </Tooltip>
        )}
      </>
    ),
  },
  {
    id: 'unit_cost',
    header: 'Unit cost',
    accessorFn: (it) => it.unit_cost_cents ?? undefined,
    meta: { align: 'right', numeric: true },
    cell: ({ row }) => formatCents(row.original.unit_cost_cents),
  },
  {
    id: 'actions',
    header: () => <span className="sr-only">Harvest</span>,
    meta: { align: 'right', cellClass: 'whitespace-nowrap' },
    cell: ({ row, table }) => {
      const { onHarvested } = table.options.meta as BinMeta;
      const it = row.original;
      return (
        <KeyPopover
          triggerLabel="Harvest"
          triggerAriaLabel={`Harvest ${itemLabel(it)}, box ${it.barcode}`}
          triggerClassName={ROW_KEY}
          tooltip="Move good units out of the bin to a shelf"
          title="Harvest"
          align="end"
        >
          {(close) => (
            <MoveForm
              receiptItemId={it.receipt_item_id}
              remaining={it.quantity_remaining}
              preferType="warehouse"
              submitLabel="Harvest"
              onCancel={close}
              onMoved={(result, dest) => {
                close();
                onHarvested(it, result.moved, dest.name);
              }}
            />
          )}
        </KeyPopover>
      );
    },
  },
];

const ScrapBinPage: React.FC = () => {
  const api = usePullsApi();
  const toast = useToast();
  const [items, setItems] = useState<ScrapBinItem[]>([]);
  const [binValue, setBinValue] = useState<number | null | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [throwError, setThrowError] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    api
      .scrapBin()
      .then((r) => {
        setItems(r.items || []);
        setBinValue(r.bin_value_cents);
        // Drop a selection that no longer exists in the bin.
        setSelected((prev) => new Set((r.items || []).map((i) => i.receipt_item_id).filter((id) => prev.has(id))));
      })
      .catch((err: any) => setError(err?.message || 'The scrap bin did not load.'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const overCount = useMemo(() => items.filter((i) => daysSince(i.binned_at) > OLD_AFTER_DAYS).length, [items]);
  const hasCost = items.some((i) => i.unit_cost_cents !== undefined);
  const chosen = items.filter((i) => selected.has(i.receipt_item_id));
  const chosenUnits = chosen.reduce((n, i) => n + i.quantity_remaining, 0);
  const allSelected = items.length > 0 && chosen.length === items.length;

  const onToggle = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const onToggleAll = useCallback(() => {
    setSelected(allSelected ? new Set() : new Set(items.map((i) => i.receipt_item_id)));
  }, [allSelected, items]);

  const onHarvested = useCallback(
    (item: ScrapBinItem, moved: number, to: string) => {
      toast.success(`Moved ${moved} ${itemLabel(item)} to ${to}`);
      load();
    },
    [load, toast],
  );

  const throwOut = async (close: () => void) => {
    setBusy(true);
    setThrowError(null);
    try {
      await api.throwOut(chosen.map((i) => ({ receipt_item_id: i.receipt_item_id })));
      toast.success(`Threw out ${chosen.length} box${chosen.length === 1 ? '' : 'es'}, ${chosenUnits} unit${chosenUnits === 1 ? '' : 's'}`);
      setSelected(new Set());
      close();
      load();
    } catch (err: any) {
      setThrowError(err?.message || 'Nothing was thrown out. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const boxes = chosen.length;

  return (
    <PageLayout width="wide"
      kind="workspace"
      className="space-y-5 pt-4"
      header={
      <PageHeader
        title="Scrap bin"
        helpTitle="About the scrap bin"
        lede="Boxes written off at Scrap, oldest first."
        help={
          <>
            <p>
              The scrap bin is everything sitting at the Scrap location: parts rejected at
              receiving, parts taken out of a drone, and parts pulled to scrap. They are off
              the shelf but still on the books.
            </p>
            <p className="font-medium text-on-surface">Thrown out</p>
            <p>
              Select the boxes that were physically discarded and press Thrown out. The units
              leave the books for good, though the history stays.
            </p>
            <p className="font-medium text-on-surface">Harvest</p>
            <p>
              If a good part can be saved from a box, press Harvest on its row and choose the
              shelf it goes to. The rest stays in the bin.
            </p>
            <p>
              Boxes that have sat here more than {OLD_AFTER_DAYS} days are counted at the
              top, because scrap that nobody decides about is stock nobody can find.
            </p>
          </>
        }
        meta={
          !loading && !error ? (
            <>
              <Tag variant="code">
                <span className={overCount > 0 ? 'text-amber-300' : ''}>{overCount}</span> over {OLD_AFTER_DAYS} days
              </Tag>
              {binValue !== undefined && (
                <Tag variant="code">
                  Bin value {formatCents(binValue)}
                </Tag>
              )}
            </>
          ) : undefined
        }
      />
      }
    >

      <Reveal show={!!error}>
        <Alert
          onRetry={() => {
            setLoading(true);
            load();
          }}
        >
          {error} Check your connection, then try again.
        </Alert>
      </Reveal>

      <DataTable<ScrapBinItem>
        data={items}
        columns={BIN_COLUMNS}
        getRowId={(i) => i.receipt_item_id}
        meta={{ selected, allSelected, onToggle, onToggleAll, onHarvested } as BinMeta}
        columnVisibility={{ unit_cost: hasCost }}
        loading={loading}
        loadingContent={<LogoSpinner label="Loading the scrap bin" />}
        strip={
          items.length === 0 ? undefined : (
          <>
            <span role="status" className="text-xs tabular-nums text-on-surface-variant">
              {boxes === 0
                ? 'Select the boxes that were thrown out.'
                : `${boxes} box${boxes === 1 ? '' : 'es'} selected, ${chosenUnits} unit${chosenUnits === 1 ? '' : 's'}`}
            </span>
            <span className="ml-auto">
              <KeyPopover
                triggerLabel="Thrown out"
                triggerClassName={TONAL_DANGER}
                tooltip="Take the selected boxes off the books"
                title="Throw out"
                disabled={boxes === 0}
                align="end"
                onOpen={() => setThrowError(null)}
              >
                {(close) => (
                  <div className="space-y-3">
                    <p className="text-sm text-on-surface">
                      Throw out {boxes} box{boxes === 1 ? '' : 'es'}, {chosenUnits} unit{chosenUnits === 1 ? '' : 's'}?
                      This takes them off the books.
                    </p>
                    <Reveal show={!!throwError}>
                      <Alert tone="error" variant="text">
                        {throwError}
                      </Alert>
                    </Reveal>
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={close}
                        className={buttonClass('quiet')}
                      >
                        Never mind
                      </button>
                      <button
                        type="button"
                        autoFocus
                        disabled={busy}
                        onClick={() => throwOut(close)}
                        className={TONAL_DANGER}
                      >
                        {busy ? 'Throwing out…' : 'Throw out'}
                      </button>
                    </div>
                  </div>
                )}
              </KeyPopover>
            </span>
          </>
          )
        }
        empty={
          <EmptyState variant="area">The scrap bin is empty.</EmptyState>
        }
      />
    </PageLayout>
  );
};

export default ScrapBinPage;
